import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useGuests, useDeleteGuest } from 'hooks/api/useGuest';
import type { Guest, GuestSearchRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import GuestModal from 'components/modals/modules/guest-module/GuestModal';
import GuestViewModal from 'components/modals/modules/guest-module/GuestViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const vipFilterOptions: SelectOption[] = [
  { value: '', label: 'All Guests' },
  { value: 'true', label: 'VIP Only' },
  { value: 'false', label: 'Non-VIP Only' },
];

const idTypeFilterOptions: SelectOption[] = [
  { value: '', label: 'All ID Types' },
  { value: 'Aadhar', label: 'Aadhar' },
  { value: 'Passport', label: 'Passport' },
  { value: 'DL', label: 'Driving License' },
  { value: 'Voter ID', label: 'Voter ID' },
  { value: 'PAN', label: 'PAN' },
  { value: 'Govt ID', label: 'Government ID' },
];

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'firstName', label: 'First Name', field: 'firstName', required: false, defaultVisible: true, sortable: true },
  { id: 'lastName', label: 'Last Name', field: 'lastName', required: false, defaultVisible: true, sortable: true },
  { id: 'title', label: 'Title', field: 'title', required: false, defaultVisible: false, sortable: false },
  { id: 'phone', label: 'Phone', field: 'phone', required: false, defaultVisible: true, sortable: false },
  { id: 'email', label: 'Email', field: 'email', required: false, defaultVisible: true, sortable: false },
  { id: 'nationality', label: 'Nationality', field: 'nationality', required: false, defaultVisible: false, sortable: true },
  { id: 'city', label: 'City', field: 'city', required: false, defaultVisible: false, sortable: true },
  { id: 'country', label: 'Country', field: 'country', required: false, defaultVisible: false, sortable: true },
  { id: 'idType', label: 'ID Type', field: 'idType', required: false, defaultVisible: true, sortable: true },
  { id: 'idNumber', label: 'ID Number', field: 'idNumber', required: false, defaultVisible: true, sortable: false },
  { id: 'isVip', label: 'VIP', field: 'isVip', required: false, defaultVisible: true, sortable: true },
  { id: 'isActive', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0, width: '320px', maxHeight: '500px',
          background: 'var(--bg-primary)', border: '2px solid var(--primary)', borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)', zIndex: 1000, animation: 'slideDown 0.2s ease',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem', borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Manage Columns</h3>
            <button onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}>
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div key={column.id} onClick={() => toggleColumn(column.id)}
                style={{ display: 'flex', alignItems: 'center', padding: '0.625rem 1.25rem', cursor: column.required ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', gap: '0.75rem', opacity: column.required ? 0.6 : 1 }}>
                <div style={{
                  width: '1.125rem', height: '1.125rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid var(--border-color)', borderRadius: '0.25rem', transition: 'all 0.2s ease', flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{ padding: '1rem', borderTop: '2px solid var(--border-color)', background: 'var(--bg-primary)', boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)', display: 'flex', flexDirection: 'column', gap: '0.75rem', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirmResetOpen} title="Reset Columns" message="Reset to default column configuration?"
        confirmLabel="Reset" cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)} />
    </div>
  );
};

// ============================================================================
// GUEST CARD COMPONENT (Grid View)
// ============================================================================

interface GuestCardProps {
  guest: Guest;
  onView: (g: Guest) => void;
  onEdit: (g: Guest) => void;
  onCopy: (g: Guest) => void;
  onDelete: (g: Guest) => void;
}

const GuestCard: React.FC<GuestCardProps> = ({ guest, onView, onEdit, onCopy, onDelete }) => {
  const menuItems: ActionMenuItem[] = [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(guest) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(guest) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => onCopy(guest) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(guest), variant: 'danger' },
  ];

  const fullName = `${guest.title ? guest.title + ' ' : ''}${guest.firstName} ${guest.lastName}`;

  return (
    <div
      style={{
        background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem',
        padding: '1.25rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', transition: 'all 0.2s ease',
        position: 'relative', display: 'flex', flexDirection: 'column', gap: '1rem',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.25rem' }}>
            {fullName}
          </h3>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {guest.email || guest.phone || '-'}
          </div>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Nationality</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{guest.nationality || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>ID Type</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{guest.idType || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>VIP</div>
          <span className={`badge badge-${guest.isVip ? 'active' : 'inactive'}`}>
            {guest.isVip ? 'Yes' : 'No'}
          </span>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${guest.isActive ? 'active' : 'inactive'}`}>
            {guest.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface GuestMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const GuestMasterScreen: React.FC<GuestMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<GuestSearchRequest>({ page: 1, pageSize: 25 });
  const { data, pagination, refetch } = useGuests(queryParams);
  const { deleteGuest, loading: isDeleting } = useDeleteGuest();

  // --- View state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterVip, setFilterVip] = useState('');
  const [filterIdType, setFilterIdType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Guest | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Column selector handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('guest-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('guest-master-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('guest-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // --- Search debounce ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- F2 search popup ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // --- View handler ---
  const handleView = useCallback((guest: Guest) => {
    const viewId = tabId ? `${tabId}-view-modal-${guest.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(guest);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((guestId: string) => {
    const guest = data.find(g => g.id === guestId);
    if (guest) { setSelectedRecord(guest); setViewModalOpen(true); }
  }, [data]);

  // --- Edit handler ---
  const handleEdit = useCallback((guest: Guest) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${guest.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(guest);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- Copy handler ---
  const handleCopy = useCallback((guest: Guest) => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(guest);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((guest: Guest) => {
    setSelectedRecord(guest);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteGuest(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.firstName} ${selectedRecord.lastName}" has been deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteGuest, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, guestId?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (guestId) {
      const guest = data.find(g => g.id === guestId);
      if (guest) setSelectedRecord(guest);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data]);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<Guest>[] = useMemo(() => [
    { id: 'firstName', header: 'First Name', accessorKey: 'firstName', size: 150, enableSorting: true },
    { id: 'lastName', header: 'Last Name', accessorKey: 'lastName', size: 150, enableSorting: true },
    { id: 'title', header: 'Title', accessorKey: 'title', size: 80,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'phone', header: 'Phone', accessorKey: 'phone', size: 140,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'email', header: 'Email', accessorKey: 'email', size: 220,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'nationality', header: 'Nationality', accessorKey: 'nationality', size: 120, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'city', header: 'City', accessorKey: 'city', size: 120, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'country', header: 'Country', accessorKey: 'country', size: 120, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'idType', header: 'ID Type', accessorKey: 'idType', size: 120, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'idNumber', header: 'ID Number', accessorKey: 'idNumber', size: 150,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'isVip', header: 'VIP', accessorKey: 'isVip', size: 80,
      cell: ({ getValue }) => {
        const isVip = getValue() as boolean;
        return <span className={`badge badge-${isVip ? 'active' : 'inactive'}`}>{isVip ? 'Yes' : 'No'}</span>;
      },
    },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>{isActive ? 'Active' : 'Inactive'}</span>;
      },
    },
  ], []);

  // --- Row actions ---
  const getRowActions = useCallback((guest: Guest): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(guest) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(guest) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(guest) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(guest), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'search', icon: <IconSystem.search size={18} />, label: 'Search Guests', onClick: () => setSearchPopupOpen(true), tooltip: 'Search Guests (F2)', variant: 'icon' },
    ];
    if (allowedActions.includes('add')) {
      buttons.push({ id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Guest', onClick: handleAddNew, tooltip: 'Add New Guest', variant: 'icon' });
    }
    buttons.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' });
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => console.log('Export clicked'), tooltip: 'Export', variant: 'icon' });
    }
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply ---
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      isVip: filterVip ? filterVip === 'true' : undefined,
      idType: filterIdType || undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterVip, filterIdType, filterStatus]);

  // --- Filter Clear ---
  const handleFilterClear = useCallback(() => {
    setFilterVip('');
    setFilterIdType('');
    setFilterStatus('');
    setQueryParams(prev => ({ ...prev, isVip: undefined, idType: undefined, includeInactive: undefined, page: 1 }));
  }, []);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Guest Master"
        description="Manage guest profiles and identity records"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : undefined}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search guests...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as ViewMode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: [filterVip, filterIdType, filterStatus].filter(Boolean).length }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={<ColumnSelector columns={columnDefinitions} visibleColumns={visibleColumns} onApply={handleApplyColumns} onReset={handleResetColumns} />}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No guests found"
              frozenLeftColumns={['select', 'firstName']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(pagination.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
                  {data.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.folder size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No guests found</p>
                    </div>
                  ) : (
                    data.map((guest) => (
                      <GuestCard key={guest.id as string} guest={guest} onView={handleView} onEdit={handleEdit} onCopy={handleCopy} onDelete={handleDeleteClick} />
                    ))
                  )}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select value={currentPageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
                        {[10, 25, 50, 100].map((size) => (<option key={size} value={size}>{size} / page</option>))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Page {currentPage} of {totalPages}</span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Filter panel */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button onClick={() => setFilterOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters">
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="VIP Status" options={vipFilterOptions} value={filterVip}
                onChange={(val: string | string[] | null) => setFilterVip((val as string) || '')}
                placeholder="Select VIP status..." isClearable={false} size="sm" />
            </div>
            <div className="filter-section">
              <SyutSelect label="ID Type" options={idTypeFilterOptions} value={filterIdType}
                onChange={(val: string | string[] | null) => setFilterIdType((val as string) || '')}
                placeholder="Select ID type..." isClearable={false} size="sm" />
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={statusOptions} value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..." isClearable={false} size="sm" />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
            <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
          </div>
        </div>}
      </div>

      {/* View Modal */}
      <GuestViewModal isOpen={viewModalOpen} guest={selectedRecord}
        onClose={() => setViewModalOpen(false)} onEdit={(guest) => handleEdit(guest)}
        tabId={tabId} moduleName={moduleName} onRestoreView={handleRestoreView} />

      {/* Form Modal */}
      <GuestModal isOpen={formModalOpen} mode={formMode} record={selectedRecord}
        onSave={handleSaveSuccess} onClose={() => setFormModalOpen(false)}
        tabId={tabId} moduleName={moduleName} onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId} copyInstanceId={copyInstanceId} minimizeRef={formModalMinimizeRef} />

      {/* Delete Confirm */}
      <ConfirmDialog open={deleteModalOpen} title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord ? `${selectedRecord.firstName} ${selectedRecord.lastName}` : ''}"? This action cannot be undone.`}
        confirmLabel="Delete" cancelLabel="Cancel" variant="danger" loading={isDeleting} loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete} onCancel={() => setDeleteModalOpen(false)} />

      {/* Guest Search Popup (F2) */}
      <SyutSearchGrid<Guest>
        isOpen={searchPopupOpen} onClose={() => setSearchPopupOpen(false)}
        onSelect={(guest) => handleView(guest)} title="Search Guests"
        columns={[
          { key: 'firstName', header: 'First Name', width: '140px', sortable: true },
          { key: 'lastName', header: 'Last Name', width: '140px', sortable: true },
          { key: 'phone', header: 'Phone', width: '140px', sortable: false },
          { key: 'email', header: 'Email', width: '200px', sortable: false },
          { key: 'idType', header: 'ID Type', width: '120px', sortable: true },
          {
            key: 'isVip', header: 'VIP', width: '80px',
            render: (value: any) => (
              <span style={{
                padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500,
                background: value ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-inactive-bg, #fee2e2)',
                color: value ? 'var(--badge-active-color, #166534)' : 'var(--badge-inactive-color, #991b1b)',
              }}>
                {value ? 'Yes' : 'No'}
              </span>
            ),
          },
          {
            key: 'isActive', header: 'Status', width: '100px',
            render: (value: any) => (
              <span style={{
                padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500,
                background: value ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-inactive-bg, #fee2e2)',
                color: value ? 'var(--badge-active-color, #166534)' : 'var(--badge-inactive-color, #991b1b)',
              }}>
                {value ? 'Active' : 'Inactive'}
              </span>
            ),
          },
        ]}
        data={data}
        filters={[
          { key: 'isVip', label: 'VIP', type: 'select', options: [{ value: 'true', label: 'VIP' }, { value: 'false', label: 'Non-VIP' }] },
          { key: 'isActive', label: 'Status', type: 'select', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
        ]}
        rowKey="id" enableRecentlyUsed recentlyUsedKey="guest-master-search"
        searchPlaceholder="Search by name, phone, email, or ID number... (F2 to focus)"
      />
    </div>
  );
};

export default GuestMasterScreen;
